import React from "react";
import { Box, Button, Divider, Drawer, IconButton } from "@material-ui/core";
import { Close, OpenInNew } from "@material-ui/icons";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Text } from "shared";
import { Area } from "types";
import { Referral } from "../queries/types";
import StatusChip from "./StatusChip";
import EditableCell from "./EditableCell";

const DRAWER_WIDTH = 420;

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: DRAWER_WIDTH,
    padding: 0,
    top: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  body: {
    overflowY: "auto",
    padding: theme.spacing(0, 2, 3),
  },
  section: {
    paddingTop: theme.spacing(2),
  },
  sectionTitle: {
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(1),
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(0.5, 0),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    flexShrink: 0,
    width: 140,
  },
  value: {
    fontSize: 13,
    textAlign: "right",
    flex: 1,
    wordBreak: "break-word",
  },
  patientLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  linkIcon: {
    fontSize: 14,
  },
  nhsMono: {
    fontFamily: "monospace",
  },
  editableRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
  },
}));

interface ReferralDetailDrawerProps {
  referral: Referral | null;
  onClose: () => void;
  onCellSave: (id: number, field: string, value: string | null) => void;
}

const formatNhsNumber = (nhs: string | null): string => {
  if (!nhs) return "-";
  const digits = String(nhs).replace(/\D/g, "");
  if (digits.length !== 10) return nhs;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

const formatBodyParts = (value: string | null): string => {
  if (!value) return "-";
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.join(", ");
  } catch {
    // plain text
  }
  return value;
};

const DetailRow: React.VFC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const classes = useStyles();
  return (
    <Box className={classes.row}>
      <span className={classes.label}>{label}</span>
      <span className={classes.value}>{value ?? "-"}</span>
    </Box>
  );
};

const ReferralDetailDrawer: React.VFC<ReferralDetailDrawerProps> = ({ referral, onClose, onCellSave }) => {
  const classes = useStyles();

  const getDisplayStatus = (r: Referral): string => {
    if (r.special_status && r.special_status.trim()) return r.special_status;
    return r.status || "";
  };

  return (
    <Drawer anchor="right" open={referral !== null} onClose={onClose} classes={{ paper: classes.paper }}>
      {referral && (
        <>
          <Box className={classes.header}>
            <Box>
              <Text bold>{referral.patient_name || "Unknown Patient"}</Text>
              <Box mt={0.5}>
                <StatusChip status={getDisplayStatus(referral)} />
              </Box>
              {referral.patient_id && (
                <Box mt={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<OpenInNew />}
                    href={`${Area.Patients}/${referral.patient_id}`}
                  >
                    View Patient
                  </Button>
                </Box>
              )}
            </Box>
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Box className={classes.body}>
            {/* Patient Info */}
            <Box className={classes.section}>
              <Text tiny bold className={classes.sectionTitle}>Patient</Text>
              <DetailRow label="Patient ID" value={referral.patient_id} />
              <DetailRow label="NHS Number" value={<span className={classes.nhsMono}>{formatNhsNumber(referral.nhs_number)}</span>} />
              <DetailRow label="DOB" value={referral.patient_dob} />
              <DetailRow label="Email" value={referral.patient_email} />
              <DetailRow label="Phone" value={referral.patient_phone_numbers} />
              <DetailRow label="Address" value={referral.patient_address} />
              <DetailRow label="GP Details" value={referral.gp_details} />
              <DetailRow label="State" value={referral.state} />
            </Box>

            <Divider />

            {/* Referral Info */}
            <Box className={classes.section}>
              <Text tiny bold className={classes.sectionTitle}>Referral</Text>
              <DetailRow label="Site" value={referral.site} />
              <DetailRow label="Urgency" value={referral.urgency} />
              <DetailRow label="Referral Date" value={referral.referral_date} />
              <DetailRow label="Processed" value={referral.referral_processed_at} />
              <DetailRow label="Invite Sent" value={referral.invitation_sent_at} />
              <DetailRow label="Days to Invite" value={referral.referral_to_invite} />
              <DetailRow label="CIC Pathway" value={referral.cic_pathway_ids} />
              <DetailRow label="Body Parts" value={formatBodyParts(referral.body_parts_referred)} />
              <DetailRow label="Invited Joint" value={referral.invited_joint} />
              {referral.wrong_joint && <DetailRow label="Wrong Joint" value={<Text tiny error>{referral.wrong_joint}</Text>} />}
              {referral.wrong_site && <DetailRow label="Wrong Site" value={<Text tiny error>{referral.wrong_site}</Text>} />}
            </Box>

            <Divider />

            {/* Treatment */}
            <Box className={classes.section}>
              <Text tiny bold className={classes.sectionTitle}>Treatment</Text>
              <DetailRow label="Signup Payer" value={referral.signup_payer_name} />
              <DetailRow label="Current Payer" value={referral.current_payer_name} />
              <DetailRow label="Therapist" value={referral.therapist_name} />
              <DetailRow label="Signed Up" value={referral.signed_up_at} />
              <DetailRow label="Treatment Start" value={referral.treatment_started_on} />
              <DetailRow label="Week" value={referral.current_course_week_number} />
              <DetailRow label="Total Contacts" value={referral.total_contacts} />
              <DetailRow label="Last Activity" value={referral.last_activity_date} />
            </Box>

            <Divider />

            {/* Care Events */}
            <Box className={classes.section}>
              <Text tiny bold className={classes.sectionTitle}>Care Events</Text>
              <DetailRow label="Kickoff Date" value={referral.kickoff_date} />
              <DetailRow label="Kickoff Status" value={referral.kickoff_booking_status} />
              <DetailRow label="Followup Date" value={referral.followup_date} />
              <DetailRow label="Followup Status" value={referral.followup_booking_status} />
              <DetailRow label="Discharge Date" value={referral.discharge_date} />
              <DetailRow label="Discharge Status" value={referral.discharge_booking_status} />
            </Box>

            <Divider />

            {/* Annotations (editable) */}
            <Box className={classes.section}>
              <Text tiny bold className={classes.sectionTitle}>Annotations</Text>
              <Box className={classes.editableRow}>
                <span className={classes.label}>Special Status</span>
                <EditableCell value={referral.special_status} field="special_status" rowId={referral.id} type="select" onSave={onCellSave} />
              </Box>
              <Box className={classes.editableRow}>
                <span className={classes.label}>Discharge Sent</span>
                <EditableCell value={referral.discharge_sent} field="discharge_sent" rowId={referral.id} type="date" onSave={onCellSave} />
              </Box>
              <Box className={classes.editableRow}>
                <span className={classes.label}>Invoice Month</span>
                <EditableCell value={referral.invoice_submission_month} field="invoice_submission_month" rowId={referral.id} type="month" onSave={onCellSave} />
              </Box>
              <Box className={classes.editableRow}>
                <span className={classes.label}>Comments</span>
                <EditableCell value={referral.comments} field="comments" rowId={referral.id} type="text" onSave={onCellSave} />
              </Box>
              <Box className={classes.editableRow}>
                <span className={classes.label}>Notes</span>
                <EditableCell value={referral.notes} field="notes" rowId={referral.id} type="text" onSave={onCellSave} />
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default ReferralDetailDrawer;
